import { createHttpClient } from '@adapters/HttpClient';
import { apiClientWH } from '@shared/services';
import { useAuthStore } from '@shared/stores/authStore';
import { useUserData } from '@shared/hooks/useUser';
import { QueryClient, useIsMutating, useMutation, useQueryClient } from '@tanstack/react-query';
import { startTransition } from 'react';
import { useTranslation } from 'react-i18next';
import { useNavigate, useSearchParams } from 'react-router-dom';
import { useToast } from '@shared/hooks/use-toast';
import { AuthApiAdapter } from '../adapters/AuthApiAdapter';
import { ForgotPasswordRequest, LoginRequest, ResetPasswordRequest } from '../models/Auth';
import { AuthService } from '../services/AuthService';

const AUTH_QUERY_KEYS = {
  user: ['auth', 'user'],
  currentUser: ['me'],
} as const;
const SWITCH_TEAM_MUTATION_KEY = ['auth', 'switch-team'] as const;
const CLIENT_DETAIL_PATH_REGEX = /^\/clients\/\d+$/;

/**
 * El switch se confirmó en backend (el token ya es el del team nuevo) pero /me falló.
 * No se puede volver al team anterior: el backend ya opera con el nuevo.
 */
class SwitchTeamProfileError extends Error {
  readonly originalError: unknown;

  constructor(originalError: unknown) {
    // El texto visible sale de i18n en onError; este mensaje es solo para depurar.
    super('Switch team profile load failed');
    this.name = 'SwitchTeamProfileError';
    this.originalError = originalError;
  }
}

/**
 * Cambia de team y sincroniza token, usuario y rol en el store sin toasts ni invalidaciones.
 * Útil para flujos internos (p. ej. crear pack en contexto de un cliente 3PL).
 */
export async function switchTeamAndSyncSession(
  teamId: number,
  deps: { authService: AuthService; queryClient: QueryClient },
): Promise<void> {
  const { authService, queryClient } = deps;
  const authStore = useAuthStore.getState();
  const response = await authService.switchTeam(teamId);
  if (!response?.token) {
    throw new Error('No se recibió token al cambiar de equipo');
  }
  authStore.setToken(response.token);
  authStore.setAuthenticated(true);
  const data = await authService.getCurrentUser();
  authStore.applyCurrentUser(data);
  queryClient.setQueryData(AUTH_QUERY_KEYS.currentUser, data);
}

type LoginTokenResponse = { token: { token: string; type?: string; expires_at?: string } };

// Lógica de éxito compartida entre login por credenciales y login con Google:
// guardar token, cargar /me, invalidar cache y redirigir.
const useHandleLoginSuccess = () => {
  const navigate = useNavigate();
  const [searchParams] = useSearchParams();
  const { setToken, setAuthenticated } = useAuthStore();
  const queryClient = useQueryClient();

  const httpClient = createHttpClient(apiClientWH);
  const apiAdapter = new AuthApiAdapter(httpClient);
  const authService = new AuthService(apiAdapter);

  // ?? Función para determinar la URL de redirección
  const getRedirectUrl = () => {
    const redirectUrl = searchParams.get('redirect');

    // Si no hay URL de redirección, ir a inicio
    if (!redirectUrl) {
      return '/home';
    }

    // Si la URL de redirección es login, ir a inicio (evitar loop)
    if (redirectUrl.startsWith('/login')) {
      return '/home';
    }

    // Si viene un detalle de cliente tras login, forzar listado para evitar exposición.
    if (CLIENT_DETAIL_PATH_REGEX.test(redirectUrl)) {
      return '/clients';
    }

    // Si es una URL válida, usarla
    return redirectUrl;
  };

  return async (response: LoginTokenResponse) => {
    // ?? 1. Guardar token primero
    setToken(response.token.token);
    setAuthenticated(true);

    try {
      // ?? 2. Obtener datos del usuario con el token guardado
      const userData = await authService.getCurrentUser();

      // ?? Desestructurar y guardar correctamente
      useAuthStore.getState().applyCurrentUser(userData);
      queryClient.setQueryData(AUTH_QUERY_KEYS.currentUser, userData);

      // ?? 3. Invalidar cache de usuario
      queryClient.invalidateQueries({ queryKey: AUTH_QUERY_KEYS.user });

      // ?? 4. Redirigir a la URL apropiada
      navigate(getRedirectUrl(), { replace: true });
    } catch (userError) {
      console.warn('Error obteniendo usuario después del login:', userError);
      // Aún redirigimos, el usuario se puede cargar después
      navigate(getRedirectUrl(), { replace: true });
    }
  };
};

// ?? Hook para login
export const useLogin = () => {
  const httpClient = createHttpClient(apiClientWH);
  const apiAdapter = new AuthApiAdapter(httpClient);
  const authService = new AuthService(apiAdapter);
  const handleLoginSuccess = useHandleLoginSuccess();

  return useMutation({
    mutationFn: (credentials: LoginRequest) => authService.login(credentials),
    onSuccess: handleLoginSuccess,
    onError: (error) => {
      console.error('Error en login:', error);
    },
  });
};

// ?? Hook para login con Google (access_token obtenido en el front vía Google Identity Services)
export const useLoginWithGoogle = () => {
  const httpClient = createHttpClient(apiClientWH);
  const apiAdapter = new AuthApiAdapter(httpClient);
  const authService = new AuthService(apiAdapter);
  const handleLoginSuccess = useHandleLoginSuccess();

  return useMutation({
    mutationFn: (accessToken: string) => authService.loginWithGoogle(accessToken),
    onSuccess: handleLoginSuccess,
    onError: (error) => {
      console.error('Error en login con Google:', error);
    },
  });
};

// ?? Hook para logout
export const useLogout = (redirectPath = '/login') => {
  const navigate = useNavigate();
  const { logout: clearAuthStore } = useAuthStore();
  const queryClient = useQueryClient();

  const httpClient = createHttpClient(apiClientWH);
  const apiAdapter = new AuthApiAdapter(httpClient);
  const authService = new AuthService(apiAdapter);

  const finalizeLogout = () => {
    startTransition(() => {
      navigate(redirectPath, { replace: true });
      clearAuthStore();
      queryClient.clear();
    });
  };

  return useMutation({
    mutationFn: () => authService.logout(),
    onSuccess: finalizeLogout,
    onError: (error) => {
      console.warn('[logout] onError servidor:', error);
      finalizeLogout();
    },
  });
};

// ?? Hook para obtener usuario actual
export const useCurrentUser = (enabled: boolean = true) => {
  const isBlockModel = useAuthStore(
    (state) => state.authorization?.model_version === 2 || state.role?.model_version === 2,
  );
  return useUserData({
    enabled,
    preserveLegacyImpersonation: true,
    revalidateOnMount: isBlockModel ? 'always' : false,
    refetchOnWindowFocus: isBlockModel ? 'always' : false,
  });
};

// ?? Hook combinado para facilidad de uso
export const useAuth = () => {
  const user = useAuthStore((s) => s.user);
  const token = useAuthStore((s) => s.token);
  const isAuthenticated = useAuthStore((s) => s.isAuthenticated);
  const loginMutation = useLogin();
  const logoutMutation = useLogout();
  const currentUserQuery = useCurrentUser();

  return {
    // Estado
    user,
    token,
    isAuthenticated,

    // Queries
    currentUser: currentUserQuery.data,
    isLoadingUser: currentUserQuery.isLoading,
    userError: currentUserQuery.error,

    // Mutations
    login: loginMutation.mutateAsync,
    logout: logoutMutation.mutateAsync,

    // Estados de loading
    isLoggingIn: loginMutation.isPending,
    isLoggingOut: logoutMutation.isPending,

    // Errores
    loginError: loginMutation.error,
    logoutError: logoutMutation.error,

    // Refetch user
    refetchUser: currentUserQuery.refetch,
  };
};

// ?? Hook para solicitar reset de contraseña
export const useRequireResetPassword = () => {
  const httpClient = createHttpClient(apiClientWH);
  const apiAdapter = new AuthApiAdapter(httpClient);
  const authService = new AuthService(apiAdapter);

  return useMutation({
    mutationFn: (request: ForgotPasswordRequest) => authService.requireResetPassword(request),
    onError: (error) => {
      console.error('Error al solicitar reset de contraseña:', error);
    },
  });
};

// ?? Hook para confirmar reset de contraseña
export const useResetPassword = () => {
  const navigate = useNavigate();
  const httpClient = createHttpClient(apiClientWH);
  const apiAdapter = new AuthApiAdapter(httpClient);
  const authService = new AuthService(apiAdapter);

  return useMutation({
    mutationFn: (request: ResetPasswordRequest) => authService.confirmResetPassword(request),
    onSuccess: () => {
      // Redirigir a login con mensaje de éexito
      navigate('/login?reset=success', { replace: true });
    },
    onError: (error) => {
      console.error('Error al resetear contraseña:', error);
    },
  });
};

// Hook para cambiar de team
export const useSwitchTeam = () => {
  const queryClient = useQueryClient();
  const { toast } = useToast();
  const { t } = useTranslation('menu');
  const httpClient = createHttpClient(apiClientWH);
  const apiAdapter = new AuthApiAdapter(httpClient);
  const authService = new AuthService(apiAdapter);
  const authStore = useAuthStore();

  return useMutation({
    mutationKey: SWITCH_TEAM_MUTATION_KEY,
    onMutate: () => {
      const previous = useAuthStore.getState();
      // Mostrar overlay reutilizando AuthInitializer (team/role en null)
      authStore.setTeam(null);
      authStore.setRole(null);
      return { previous } as const;
    },
    mutationFn: async (teamId: number) => {
      // 1. Cambiar de team y recibir nuevo token
      const response = await authService.switchTeam(teamId);
      if (!response?.token) {
        throw new Error('No se recibio token al cambiar de equipo');
      }

      // 2. Guardar token y marcar autenticado
      authStore.setToken(response.token);
      authStore.setAuthenticated(true);

      // 3. Traer datos actualizados del usuario (incluye team/role actuales)
      let data;
      try {
        data = await authService.getCurrentUser();
      } catch (error) {
        throw new SwitchTeamProfileError(error);
      }

      // 4. Actualizar store completo
      authStore.applyCurrentUser(data);
      queryClient.setQueryData(AUTH_QUERY_KEYS.currentUser, data);

      return { response, userData: data };
    },
    onSuccess: () => {
      // 5. Refrescar queries dependientes del team/rol/token
      queryClient.invalidateQueries();

      // 6. Mostrar feedback
      toast({
        title: t('switchTeam.success.title'),
        description: t('switchTeam.success.description'),
        variant: 'default',
      });
    },
    onError: (error: any, _teamId, context) => {
      if (error instanceof SwitchTeamProfileError) {
        // El backend ya opera con el team nuevo: team/role siguen nulos (overlay) y se descarta
        // el perfil anterior en caché para que /me se vuelva a pedir con el token nuevo.
        queryClient.resetQueries({ queryKey: AUTH_QUERY_KEYS.currentUser });
        queryClient.invalidateQueries({
          predicate: (query) => query.queryKey[0] !== AUTH_QUERY_KEYS.currentUser[0],
        });
      } else if (context && useAuthStore.getState().token === context.previous.token) {
        const { user, team, role, settings, authorization } = context.previous;
        useAuthStore.setState({ user, team, role, settings, authorization });
      }

      let errorMessage = t('switchTeam.error.unexpected');
      if (error instanceof SwitchTeamProfileError) {
        errorMessage = t('switchTeam.error.profileLoad');
      } else if (error?.response?.data?.error?.message) {
        errorMessage = error.response.data.error.message;
      } else if (error?.response?.data?.message) {
        errorMessage = error.response.data.message;
      } else if (error?.message) {
        errorMessage = error.message;
      }

      toast({
        title: t('switchTeam.error.title'),
        description: errorMessage,
        variant: 'destructive',
      });
    },
  });
};

// Hook especifico para AuthInitializer (sin navegacion)
export const useAuthData = () => {
  const { user, token, isAuthenticated } = useAuthStore();
  const isSwitchingTeam = useIsMutating({ mutationKey: SWITCH_TEAM_MUTATION_KEY }) > 0;
  const currentUserQuery = useCurrentUser(!isSwitchingTeam);
  return {
    user,
    token,
    isAuthenticated,
    currentUser: currentUserQuery.data,
    isLoadingUser: currentUserQuery.isLoading,
    isRefetchingUser: currentUserQuery.isFetching,
    userError: currentUserQuery.error,
    refetchUser: currentUserQuery.refetch,
  };
};
